<script setup>
import emblaCarouselVue from 'embla-carousel-vue'
import { onMounted, ref } from 'vue'

const props = defineProps({
  current: {
    type: Number,
    default: 0,
  },
  showCurrent: {
    type: Boolean,
    default: false,
  },
  showPagination: {
    type: Boolean,
    default: false,
  },
  wrapAround: {
    type: Boolean,
    default: true,
  },
  controls: {
    type: Boolean,
    default: true,
  },
  align: {
    type: String,
    default: 'center',
  },
  slides: {
    type: Object,
    required: true,
  },
})

const [emblaRef, emblaApi] = emblaCarouselVue({
  loop: props.wrapAround,
  skipSnaps: true,
  align: props.align,
  inViewThreshold: 1,
})

const currentSlide = ref(props.current)
const maxSlide = ref(0)
const minSlide = ref(0)
const thumbWidth = ref('50%')

function next() {
  if (emblaApi.value)
    emblaApi.value.scrollNext()
}

function prev() {
  if (emblaApi.value)
    emblaApi.value.scrollPrev()
}

function changeSlide(event) {
  if (emblaApi.value) {
    emblaApi.value.scrollTo(event.target.value)
  }
}

const augmentedSlides = computed(() => {
  if (props.slides.length >= 6 || props.slides.length === 1)
    return props.slides

  return [...props.slides, ...props.slides]
})

onMounted(() => {
  if (emblaApi.value) {
    maxSlide.value = props.slides.length - 1
    thumbWidth.value = `${100 / props.slides.length}%`
    emblaApi.value.on('scroll', () => {
      const selectedSnap = emblaApi.value.selectedScrollSnap()
      const isDoubled = augmentedSlides.value.length > props.slides.length

      currentSlide.value = isDoubled
        ? selectedSnap % props.slides.length
        : selectedSnap
    })
  }
})
</script>

<template>
  <div class="slider">
    <div class="slider__carousel" data-cy="slider-carousel">
      <div
        ref="emblaRef"
        class="slider__fade-out"
      >
        <div class="embla__container">
          <div
            v-for="(slide, index) of augmentedSlides"
            :key="slide"
            class="embla__slide slider__slide"
            :class="{
              'slider__slide--first': index === 0 && !wrapAround,
              'slider__slide--last':
                index === slides.length - 1 && !wrapAround,
            }"
            :style="{ 'z-index': slides.length - index }"
          >
            <slot
              name="default"
              v-bind="slide"
              :index="index"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="slider__navigation d-flex flex-column">
      <input
        v-if="showPagination"
        id="slider"
        v-model="currentSlide"
        type="range"
        step="1"
        :min="minSlide"
        :max="maxSlide"
        class="slider__pagination"
        @input="changeSlide"
      >
      <div
        v-if="controls"
        class="d-flex justify-content-between slider__controls"
      >
        <FormButton
          class="l-button--secondary l-button--icon-only slider__button"
          :size40="true"
          :disabled="!wrapAround && currentSlide === minSlide"
          data-cy="slider-button-prev"
          @click="prev"
        >
          <IconsArrowLeft />
        </FormButton>
        <div
          v-if="showCurrent"
          class="equal-base equal-base--tighter"
        >
          <span class="slider__current">{{ currentSlide + 1 }}</span>
          <span class="slider__count"> / {{ slides.length }}</span>
        </div>
        <FormButton
          class="l-button--secondary l-button--icon-only slider__button"
          data-cy="slider-button-next"
          :size40="true"
          :disabled="!wrapAround && currentSlide === maxSlide"
          @click="next"
        >
          <IconsArrowRight />
        </FormButton>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '~/assets/scss/variables.scss';
@import 'bootstrap/scss/mixins';

.slider {
  &__navigation {
    display: flex;
    justify-content: space-between;
    margin-top: 15px;
  }

  &__carousel {
    position: relative;
  }
  .is-dragging {
    cursor: grabbing;
    .carousel__slide {
      pointer-events: none;
    }
  }

  &__slide {
    padding-right: 0 !important;
    padding-left: 0 !important;
    margin-right: 15px;
    @media (min-width: 922px) {
      margin-right: 30px;
    }
  }

  &__slide-overflow {
    padding-left: 120px !important;

    @include media-breakpoint-down(1760) {
      padding-left: 60px !important;
    }

    @include media-breakpoint-down(880) {
      padding-left: $padding-container !important;
    }
  }

  &__controls {
    position: relative;
  }

  &__current {
    position: absolute;
    right: 50%;
    bottom: 50%;
    transform: translateX(-8px) translateY(50%);
  }

  &__count {
    position: absolute;
    left: 50%;
    bottom: 50%;
    transform: translateX(-2px) translateY(50%);
  }

  &__button {
    background-color: $color-dark-02;
    border-color: $color-dark-02;
  }

  &__pagination {
    padding-bottom: 15px;
  }

  &--fade-out {
    .slider__fade-out {
      position: relative;
      overflow: clip;
      overflow-clip-margin: 120px;

      &::before {
        margin-left: -120px;
        pointer-events: none;
        z-index: 1;
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        width: 160px;
        background: linear-gradient(to left, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.8) 70%, rgba(0, 0, 0, 1) 100%);
        @media (max-width: 768px) {
          margin-left: -60px;
          width: 80px;
        }
      }

      &::after {
        pointer-events: none;
        content: '';
        margin-right: -120px;
        position: absolute;
        top: 0;
        right: 0;
        height: 100%;
        width: 160px;
        background: linear-gradient(to right, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.8) 70%, rgba(0, 0, 0, 1) 100%);
        @media (max-width: 768px) {
          margin-right: -60px;
          width: 80px;
        }
      }
    }
  }

  &--overflow {
    :deep(.carousel) {
      margin-left: -120px;
      margin-right: -120px;

      @include media-breakpoint-down(1760) {
        margin-left: -60px;
        margin-right: -60px;
      }

      @include media-breakpoint-down(880) {
        margin-left: $padding-container-negative;
        margin-right: $padding-container-negative;
      }
    }

    :deep(.slider__slide--first) {
      padding-left: 120px !important;

      @include media-breakpoint-down(1760) {
        padding-left: 60px !important;
      }

      @include media-breakpoint-down(880) {
        padding-left: $padding-container !important;
      }
    }

    :deep(.slider__slide--last) {
      padding-right: 120px !important;

      @include media-breakpoint-down(1760) {
        padding-right: 60px !important;
      }

      @include media-breakpoint-down(880) {
        padding-right: $padding-container !important;
      }
    }
  }

  &--picker {
    :deep(.carousel__viewport) {
      overflow: unset;
    }

    .slider__fade-out {
      overflow: unset;
    }
  }
}

// Styling Cross-Browser Compatible Range Inputs with Sass
// Github: https://github.com/darlanrod/input-range-sass
// Author: Darlan Rod https://github.com/darlanrod
// Version 1.5.3
// MIT License

$track-color: #262626 !default;
$thumb-color: #333333 !default;

$thumb-radius: 2px !default;
$thumb-height: 2px !default;
$thumb-width: v-bind(thumbWidth) !default;
$thumb-shadow-size: 0px !default;
$thumb-shadow-blur: 0px !default;
$thumb-shadow-color: rgba(0, 0, 0, 0.2) !default;
$thumb-border-width: 0px !default;
$thumb-border-color: #000000 !default;

$track-width: 100% !default;
$track-height: 2px !default;
$track-shadow-size: 0px !default;
$track-shadow-blur: 0px !default;
$track-shadow-color: rgba(0, 0, 0, 0.2) !default;
$track-border-width: 0px !default;
$track-border-color: #000000 !default;

$track-radius: 2px !default;
$contrast: 5% !default;

$ie-bottom-track-color: darken($track-color, $contrast) !default;

@mixin shadow($shadow-size, $shadow-blur, $shadow-color) {
  box-shadow:
    $shadow-size $shadow-size $shadow-blur $shadow-color,
    0 0 $shadow-size lighten($shadow-color, 5%);
}

@mixin track {
  cursor: default;
  height: $track-height;
  transition: all 0.2s ease;
  width: $track-width;
}

@mixin thumb {
  @include shadow($thumb-shadow-size, $thumb-shadow-blur, $thumb-shadow-color);
  background: $thumb-color;
  border: $thumb-border-width solid $thumb-border-color;
  border-radius: $thumb-radius;
  box-sizing: border-box;
  cursor: grab;
  height: $thumb-height;
  width: $thumb-width;
}

[type='range'] {
  -webkit-appearance: none;
  background: transparent;
  margin: calc($thumb-height / 2) 0;
  width: $track-width;

  &::-moz-focus-outer {
    border: 0;
  }

  &:focus {
    outline: 0;
  }

  &::-webkit-slider-runnable-track {
    @include track;
    @include shadow($track-shadow-size, $track-shadow-blur, $track-shadow-color);
    background: $track-color;
    border: $track-border-width solid $track-border-color;
    border-radius: $track-radius;
  }

  &::-webkit-slider-thumb {
    @include thumb;
    -webkit-appearance: none;
    margin-top: (calc(($track-border-width * -2 + $track-height) / 2 - ($thumb-height / 2)));
  }

  &::-moz-range-track {
    @include shadow($track-shadow-size, $track-shadow-blur, $track-shadow-color);
    @include track;
    background: $track-color;
    border: $track-border-width solid $track-border-color;
    border-radius: $track-radius;
    height: calc($track-height / 2);
  }

  &::-moz-range-thumb {
    @include thumb;
  }

  &::-ms-track {
    @include track;
    background: transparent;
    border-color: transparent;
    border-width: calc($track-height / 2) 0;
    color: transparent;
  }

  &::-ms-fill-lower {
    @include shadow($track-shadow-size, $track-shadow-blur, $track-shadow-color);
    background: $ie-bottom-track-color;
    border: $track-border-width solid $track-border-color;
    border-radius: ($track-radius * 2);
  }

  &::-ms-fill-upper {
    @include shadow($track-shadow-size, $track-shadow-blur, $track-shadow-color);
    background: $track-color;
    border: $track-border-width solid $track-border-color;
    border-radius: ($track-radius * 2);
  }

  &::-ms-thumb {
    @include thumb;
    margin-top: calc($track-height / 4);
  }

  &:disabled {
    &::-webkit-slider-thumb,
    &::-moz-range-thumb,
    &::-ms-thumb,
    &::-webkit-slider-runnable-track,
    &::-ms-fill-lower,
    &::-ms-fill-upper {
      cursor: not-allowed;
    }
  }
}
</style>
